import { cn, Flex, Icon, type IconType } from '@kognitos/lattice';

/**
 * Props for the TimelineBar component.
 */
interface ITimelineBarProps {
  /** Lucide icon name from the design system */
  iconType: IconType;
  /** Whether to hide the top separator line */
  hideTopLine?: boolean;
  /** Whether to hide the bottom separator line */
  hideBottomLine?: boolean;
  /** Whether to render lines as dashed instead of solid */
  dotted?: boolean;
  /** Optional class name override for the icon color */
  iconClassName?: string;
}

/**
 * Timeline bar component for the left side of a run log sub-step.
 *
 * Renders a vertical line with an icon in between, used to visually
 * connect timeline events. Supports solid and dotted line variants.
 *
 * @param {ITimelineBarProps} props - Component props
 * @returns {JSX.Element} Timeline bar with icon and separator lines
 */
export const TimelineBar = ({
  iconType,
  hideTopLine = false,
  hideBottomLine = false,
  dotted = false,
  iconClassName,
}: ITimelineBarProps) => {
  const lineClass = dotted
    ? 'border-l border-dashed border-border'
    : 'bg-border w-px';

  return (
    <Flex
      direction="column"
      align="center"
      gap={2}
      className="self-stretch px-0.5"
    >
      {!hideTopLine && <div className={cn('h-3', lineClass)} />}
      <Icon
        type={iconType}
        size="xs"
        className={cn('shrink-0 text-muted-foreground', iconClassName)}
      />
      {!hideBottomLine && <div className={cn('flex-1', lineClass)} />}
    </Flex>
  );
};
